import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "./Register.css";
import "react-toastify/dist/ReactToastify.css";
import { useFormWithValidation } from "../../Hook/useFormWithValidation";
import { FiEye, FiEyeOff } from "react-icons/fi";
//import GoogleSignInButton from "../Auth/GoogleSignInButton";

// Централизованные ссылки/версии документов
const CONSENT_META = {
  PRIVACY_VERSION: "v1.0 (2025-09-25)",
  TERMS_VERSION: "v1.0 (2025-09-25)",
  PRIVACY_URL: "/docs/privacy",
  TERMS_URL: "/docs/terms",
};

function Register({ handelRegister, handleGoogleLogin, handleGoogleError }) {
  const { values, handleChange, errors, isValid, resetForm } =
    useFormWithValidation();

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [consents, setConsents] = useState({
    pdn: false,   // согласие на обработку ПДн
    terms: false, // пользовательское + оферта
  });

  const togglePasswordVisibility = () => setPasswordVisible((v) => !v);

  // ВАЖНО: оставляем required, но дергаем handleChange, чтобы форма стала валидной
  const handleConsentChange = (e) => {
    const { name, checked } = e.target;
    setConsents((prev) => ({ ...prev, [name]: checked }));
    handleChange(e); // пересчет form.checkValidity()
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
      ...values,
      consents: {
        pdn: consents.pdn,
        terms: consents.terms,
        privacyVersion: CONSENT_META.PRIVACY_VERSION,
        termsVersion: CONSENT_META.TERMS_VERSION,
        privacyUrl: CONSENT_META.PRIVACY_URL,
        termsUrl: CONSENT_META.TERMS_URL,
        timestamp: new Date().toISOString(),
      },
    };

    handelRegister(payload);
    resetForm();
    setConsents({ pdn: false, terms: false });
  };

  const canSubmit = isValid && consents.pdn && consents.terms;

  return (
    <>
      <Helmet>
        <title>Регистрация — Школа КТМ</title>
        <meta name="robots" content="noindex,follow" />
        <link rel="canonical" href="https://kt-mebel.ru/sign-up" />
      </Helmet>
      <form className="form" onSubmit={handleSubmit} noValidate>
      <Link className="header-container__logo logo_form" to="/" />
      <h2 className="form__name">Регистрация</h2>

      {/* Имя */}
      <label className="form__input-name">
        Имя
        <input
          className="form__input"
          type="text"
          id="name"
          name="name"
          minLength={2}
          maxLength={30}
          pattern="^[а-яА-ЯёЁa-zA-Z0-9\s\-]+$"
          required
          value={values.name || ""}
          onChange={handleChange}
          placeholder="Введите ваше имя"
          autoComplete="name"
        />
        <span className="form__imput-error">{errors.name}</span>
      </label>

      {/* E-mail */}
      <label className="form__input-name">
        E-mail
        <input
          className={`form__input ${errors.email ? "form__input_error" : ""}`}
          type="email"
          id="email"
          name="email"
          required
          value={values.email || ""}
          onChange={handleChange}
          placeholder="Введите ваш e-mail"
          inputMode="email"
          autoComplete="email"
        />
        <span className="form__imput-error">{errors.email}</span>
      </label>

      {/* Телефон */}
      <label className="form__input-name">
        Телефон
        <input
          className={`form__input ${errors.phone ? "form__input_error" : ""}`}
          type="tel"
          id="phone"
          name="phone"
          required
          value={values.phone || ""}
          onChange={handleChange}
          placeholder="Введите номер телефона"
          inputMode="tel"
          autoComplete="tel"
          pattern="^\+?[1-9]\d{9,14}$"
          title="Формат: +7XXXXXXXXXX, 10–15 цифр"
        />
        <span className="form__imput-error">{errors.phone}</span>
      </label>

      {/* Пароль */}
      <label className="form__input-name">
        Пароль
        <div className="password-container">
          <input
            className="form__input form__input_password"
            id="password"
            name="password"
            required
            type={passwordVisible ? "text" : "password"}
            value={values.password || ""}
            onChange={handleChange}
            placeholder="Придумайте пароль"
            minLength={8}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="password-toggle-btn"
            aria-label={passwordVisible ? "Скрыть пароль" : "Показать пароль"}
            aria-pressed={passwordVisible}
            onClick={togglePasswordVisibility}
          >
            {passwordVisible ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>
        <span className="form__hint">
          Минимум 8 символов. Рекомендуем буквы верхнего/нижнего регистра и цифры.
        </span>
        <span className="form__imput-error">{errors.password}</span>
      </label>

      {/* Согласия (2 обязательных чекбокса) */}
      <div className="form__consents">
        <label className="form__checkbox">
          <input
            type="checkbox"
            name="pdn"
            checked={consents.pdn}
            onChange={handleConsentChange}
            required
          />
          <span>
            Даю согласие на{" "}
            <Link to={CONSENT_META.PRIVACY_URL} target="_blank" rel="noopener noreferrer">
              обработку персональных данных
            </Link>.
          </span>
        </label>

        <label className="form__checkbox">
          <input
            type="checkbox"
            name="terms"
            checked={consents.terms}
            onChange={handleConsentChange}
            required
          />
          <span>
            Подтверждаю, что ознакомлен(а) и согласен(сна) с{" "}
            <Link to={CONSENT_META.TERMS_URL} target="_blank" rel="noopener noreferrer">
              Пользовательским соглашением
            </Link>{" "}
            и{" "}
            <Link to="/docs/offer" target="_blank" rel="noopener noreferrer">
              Договором оферты
            </Link>.
          </span>
        </label>
      </div>

      {/* Юр. заметки */}
      <p className="form__legal-note">
        Оператор персональных данных: Тарасов Евгений Александрович, самозанятый (НПД), ИНН 382801892154, г. Красноярск.
        Контакт: <a href="mailto:ktm-online@mail.ru">ktm-online@mail.ru</a>
      </p>
      <p className="form__legal-note">
        По итогам обучения выдается именной электронный сертификат. Сертификат не является документом государственного образца.
      </p>

      <span className="form__error">Произошла ошибка, попробуйте ещё раз</span>

      <button className="form__button" type="submit" disabled={!canSubmit}>
        Зарегистрироваться
      </button>

      {/* Кнопка Google */} {/*<GoogleSignInButton
        onCredential={handleGoogleLogin}
        onError={handleGoogleError}
        text="signup_with"
      />*/}

      <div className="form__signin">
        <p className="form__signin-question">Уже зарегистрированы?</p>
        <Link to="/sign-in" className="form__login-link">Войти</Link>
      </div>
    </form>
    </>
  );
}

export default Register;
